import http from "../utils/request";
import type { ShopPage, ShopResponse } from "../types/shopMerchant";

export interface ShopOrderQuery {
  orderNo?: string; buyerKeyword?: string; goodsName?: string;
  orderStatus?: number; goodsType?: number; merchantName?: string;
  startTime?: string; endTime?: string; ypageNum: number; ypageSize: number;
  finishStartTime?: string; finishEndTime?: string;
}
export interface ShopOrderSummary {
  totalBuyNum: number;
  totalSaleAmount: number;
  settlementAmount: number;
}
export interface ShopOrderRow {
  id: number | string; itemId?: number | string; orderNo: string;
  appUserId: number | string; nickName?: string; mobile?: string;
  goodsName?: string; skuName?: string; goodsType?: number;
  merchantName?: string; supplierName?: string; goodsNum?: number;
  selfSupplier?: string | null; finishTime?: string | null;
  costPrice?: number | null;
  settlementAmount?: number | null;
  platformCommissionAmount?: number | null;
  goodsPrice?: number; payAmount?: number; orderStatus: number;
  orderStatusDesc?: string; receiveAddress?: string; postageAmount?: number; createTime?: string;
  receiverName?: string; receiverPhone?: string; totalAmount?: number;
  merchantIncome?: number | null;
  /** 商户主表让利比例（百分数），仅入驻商家订单返回。 */
  profitRatio?: number | null;
  settlementProfitRatio?: number | null;
  userRewardAmount?: number | null; userRewardFrozenAmount?: number | null; userRewardRefundAmount?: number | null;
  payTime?: string; logisticsCompany?: string; logisticsCode?: string;
}
export interface ShopOrderAftersale {
  id: number | string; aftersaleNo?: string | null;
  aftersaleType: number; aftersaleStatus: number;
  goodsName?: string | null; skuName?: string | null; applyNum?: number | null;
  applyReason?: string | null; applyImages?: string | null; refundAmount?: number | null;
  auditRemark?: string | null; auditTime?: string | null; createTime?: string | null;
  returnLogisticsCompany?: string | null; returnLogisticsCode?: string | null;
  userReturnTime?: string | null; receiveReturnTime?: string | null;
  refundTime?: string | null; refundNo?: string | null;
  returnAddressId?: number | null; returnReceiverName?: string | null;
  returnReceiverPhone?: string | null; returnAddress?: string | null;
}
export interface ShopOrderDelivery {
  orderId: number | string; logisticsCompany: string; logisticsCode: string;
}
export interface ShopOrderRemarkInput {
  orderId: number | string; remarkContent: string; image?: string;
}
export interface ShopOrderRemark {
  id: number | string; orderId: number | string; remarkContent: string;
  image?: string | null; createBy?: string | null; createTime?: string | null;
}
export function addShopOrderRemark(data: ShopOrderRemarkInput): Promise<ShopResponse<string>> {
  return http.post<never, ShopResponse<string>>("/shop/order/remark/add", data);
}
export function listShopOrderRemarks(orderId: number | string, signal?: AbortSignal): Promise<ShopResponse<ShopOrderRemark[]>> {
  return http.post<never, ShopResponse<ShopOrderRemark[]>>("/shop/order/remark/list", { orderId }, { signal });
}
export type ShopOrderRefundAction = "confirmRefund" | "rejectRefund" | "confirmReceive" | "refuseRefund";
// 订单详情直接返回的账目流水；amountType 使用账目枚举，与 payType 不同。
export interface ShopOrderReward {
  id: number | string;
  userId: number | string;
  nickName?: string | null;
  mobile?: string | null;
  accountTrend?: string | null;
  accountTrendStr?: string | null;
  businessId?: number | string;
  businessOrderType?: string | null;
  businessType?: string | null;
  businessTypeStr?: string | null;
  amountType?: string | null;
  amountTypeStr?: string | null;
  amount?: number | string | null;
  beforeAmount?: number | string | null;
  afterAmount?: number | string | null;
  freezeFlag?: number | null;
  createTime?: string | null;
  remark?: string | null;
}
export interface ShopOrderDetail {
  platformCommissionAmount?: number | null;
  userRewardAmount?: number | null; userRewardFrozenAmount?: number | null; userRewardRefundAmount?: number | null;
  order: {
    id: number | string; orderNo: string; appUserId: number | string;
    orderStatus: number; orderStatusDesc?: string;
    createTime?: string | null; payTime?: string | null;
    totalAmount?: number | null; payAmount?: number | null; postageAmount?: number | null;
    discountAmount?: number | null;
    receiverName?: string | null; receiverPhone?: string | null;
    receiverProvince?: string | null; receiverCity?: string | null;
    receiverDistrict?: string | null; receiverAddress?: string | null;
    logisticsCompany?: string | null; logisticsCode?: string | null;
    deliveryStatus?: number | null; deliveryTime?: string | null; receiveTime?: string | null; userNote?: string | null;
    isCommission?: number | null; commissionStatus?: number | null;
    settlementProfitRatio?: number | null; merchantIncome?: number | null;
  };
  orderItems: {
    id: number | string; goodsName?: string; skuName?: string; goodsImage?: string;
    goodsPrice?: number | null; goodsNum?: number; subTotal?: number | null;
  }[];
  payItems: {
    id: number | string; payType: number; payAmount?: number | null;
    serviceCharge?: number | null; payStatus: number; outTradeNo?: string | null;
  }[];
  userInfo?: { nickName?: string | null; mobile?: string | null } | null;
  aftersale?: ShopOrderAftersale | null;
  rewards?: ShopOrderReward[] | null;
}
export function deliverShopOrder(data: ShopOrderDelivery): Promise<ShopResponse<string>> {
  return http.post<never, ShopResponse<string>>("/shop/order/deliver", data);
}
export function selfPickupShopOrder(orderId: number | string): Promise<ShopResponse<string>> {
  return http.post<never, ShopResponse<string>>("/shop/order/selfPickup", { orderId });
}
export function processShopOrderRefund(action: ShopOrderRefundAction, orderId: number | string, auditRemark?: string, returnAddressId?: number): Promise<ShopResponse<string>> {
  return http.post<never, ShopResponse<string>>(`/shop/order/${action}`, {
    orderId,
    // 自营退货退款传入所选地址；仅退款和其他操作不传。
    ...(action === "confirmRefund" && returnAddressId !== undefined ? { returnAddressId } : {}),
    ...(action === "rejectRefund" || action === "refuseRefund" ? { auditRemark: auditRemark?.trim() } : {}),
  });
}
export function getShopOrder(id: number | string, signal?: AbortSignal): Promise<ShopResponse<ShopOrderDetail>> {
  return http.get<never, ShopResponse<ShopOrderDetail>>(`/shop/order/detail/${id}`, { signal });
}
export function listShopOrders(query: ShopOrderQuery, signal?: AbortSignal): Promise<ShopPage<ShopOrderRow>> {
  return http.post<never, ShopPage<ShopOrderRow>>("/shop/order/list", query, { signal });
}
export function getShopOrderSummary(query: ShopOrderQuery, signal?: AbortSignal): Promise<ShopResponse<ShopOrderSummary>> {
  const { ypageNum, ypageSize, ...filters } = query;
  return http.post<never, ShopResponse<ShopOrderSummary>>("/shop/order/shopOrderSummary", filters, { signal });
}
export async function exportShopOrders(query: ShopOrderQuery): Promise<Blob> {
  const { ypageNum, ypageSize, ...filters } = query;
  const blob = await http.post<never, Blob>("/shop/order/export", filters, { responseType: "blob" });
  if (!(blob instanceof Blob) || !blob.size || /json|text\//i.test(blob.type))
    throw new Error("导出失败，服务未返回 Excel 文件");
  return blob;
}
